/**
 * `Chapter 12 · Day 3, evening · Rust Market` — the story feed's dateline header.
 *
 * **This file used to be 130 lines of reconstruction.** The stored `Chapter` carried no
 * world time and no location, so the header was rebuilt from `changes`: `time_advanced` for
 * the clock, `moved` for the place, carried forward across chapters that recorded neither,
 * back-filled before the first move, joined to display names through `GET /stories/{id}/state`.
 * It worked and it was tested, and it still could not answer for the oldest chapter in a
 * loaded page — the engine records `time_advanced` only when the clock crosses a bucket, so
 * a page that opens mid-day has nothing to read.
 *
 * The API now denormalizes both onto the chapter row at commit (`chapters.time_*`,
 * `chapters.location_*`), which is where they were always known. What is left is formatting.
 */
import type { Chapter, GameTime, Season, TimeOfDay } from '@/api/generated';
import type { Translate } from '@/i18n/t';

/**
 * The clock's vocabulary is a closed enum on the wire and a word on screen, so it is a key
 * lookup here — unlike `location.display_name`, which the DM wrote and `quest/api/display.py`
 * already localized on its way out. The rule that tells the two apart: the engine's enums are
 * ours to name, the world's nouns are the story's.
 */
const timeOfDay = (t: Translate, value: TimeOfDay): string => t(`ui.time.of_day.${value}`);
const season = (t: Translate, value: Season): string => t(`ui.time.season.${value}`);

/**
 * `Day 6, evening · summer` — the character panel's clock.
 *
 * Here rather than in `features/character/` because the two helpers above are the only copy of
 * the `TimeOfDay` vocabulary in the client, and a second copy would drift. The season appears
 * only in the panel: the feed's dateline is per chapter and a season that changes four times a
 * story is not news at that scale.
 */
export function gameTimeLabel(t: Translate, time: GameTime): string {
  return t('ui.time.game_time', {
    day: time.day,
    time_of_day: timeOfDay(t, time.time_of_day),
    season: season(t, time.season),
  });
}

/** Everything the header needs — a stored chapter has all of it, and so does a live one. */
export type DatelineSource = Pick<Chapter, 'number' | 'world_time' | 'location'>;

/**
 * `['Chapter 12', 'Day 3, evening', 'Rust Market']` — the segments the header joins with `·`.
 * Segments rather than one string, because each is a separate key: a locale that wants a
 * different order gets it by reordering here, and one that wants different punctuation between
 * them is `Scene`'s business, not this file's.
 */
export function datelineSegments(t: Translate, chapter: DatelineSource): string[] {
  return [
    t('ui.chapter.number', { chapter: chapter.number }),
    t('ui.chapter.dateline_day', {
      day: chapter.world_time.day,
      time_of_day: timeOfDay(t, chapter.world_time.time_of_day),
    }),
    chapter.location.display_name,
  ];
}
